import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUsersApiService } from '../../../../apis/users/useUsersApiService';
import {
  GuestUserParams,
  CreateGuestUserRequest,
  CreateServiceRequest,
  UpdateServiceStatusRequest,
} from '../../../../apis/users/users.api.interfaces';

// Query Keys
export const GUEST_USERS_QUERY_KEY = 'guest-users';
export const GUEST_USER_DETAIL_QUERY_KEY = 'guest-user-detail';
export const GUESTS_SUMMARY_QUERY_KEY = 'guests-summary';

export const useGuestsList = (params: GuestUserParams, enabled = true) => {
  const { getGuestUsers } = useUsersApiService();

  return useQuery({
    queryKey: [GUEST_USERS_QUERY_KEY, params],
    queryFn: () => getGuestUsers(params),
    enabled,
  });
};

export const useGuestUserById = (id: string, enabled = true) => {
  const { getGuestUserById } = useUsersApiService();

  return useQuery({
    queryKey: [GUEST_USER_DETAIL_QUERY_KEY, id],
    queryFn: () => getGuestUserById(id),
    enabled: enabled && !!id,
  });
};

export const useGuestsSummary = (enabled = true) => {
  const { getGuestsSummary } = useUsersApiService();

  return useQuery({
    queryKey: [GUESTS_SUMMARY_QUERY_KEY],
    queryFn: () => getGuestsSummary(),
    enabled,
  });
};

export const useCreateGuestUser = () => {
  const queryClient = useQueryClient();
  const { createGuestUser } = useUsersApiService();

  return useMutation({
    mutationFn: (data: CreateGuestUserRequest) => createGuestUser(data),
    onSuccess: () => {
      // Invalidate and refetch guest users list
      queryClient.invalidateQueries({ queryKey: [GUEST_USERS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [GUESTS_SUMMARY_QUERY_KEY] });
    },
  });
};

export const useUpdateGuestUser = () => {
  const queryClient = useQueryClient();
  const { updateGuestUser } = useUsersApiService();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<CreateGuestUserRequest>;
    }) => updateGuestUser(id, data),
    onSuccess: (_, variables) => {
      // Invalidate and refetch guest users list
      queryClient.invalidateQueries({ queryKey: [GUEST_USERS_QUERY_KEY] });
      // Invalidate specific guest user detail
      queryClient.invalidateQueries({
        queryKey: [GUEST_USER_DETAIL_QUERY_KEY, variables.id],
      });
      queryClient.invalidateQueries({ queryKey: [GUESTS_SUMMARY_QUERY_KEY] });
    },
  });
};

export const useDeleteGuestUser = () => {
  const queryClient = useQueryClient();
  const { deleteGuestUser } = useUsersApiService();

  return useMutation({
    mutationFn: (id: string) => deleteGuestUser(id),
    onSuccess: () => {
      // Invalidate and refetch guest users list
      queryClient.invalidateQueries({ queryKey: [GUEST_USERS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [GUESTS_SUMMARY_QUERY_KEY] });
    },
  });
};

export const useAddServiceToGuest = () => {
  const queryClient = useQueryClient();
  const { addServiceToGuest } = useUsersApiService();

  return useMutation({
    mutationFn: ({
      id,
      service,
    }: {
      id: string;
      service: CreateServiceRequest;
    }) => addServiceToGuest(id, service),
    onSuccess: (_, variables) => {
      // Invalidate specific guest user detail to refresh services
      queryClient.invalidateQueries({
        queryKey: [GUEST_USER_DETAIL_QUERY_KEY, variables.id],
      });
    },
  });
};

export const useUpdateServiceStatus = () => {
  const queryClient = useQueryClient();
  const { updateServiceStatus } = useUsersApiService();

  return useMutation({
    mutationFn: ({
      guestId,
      serviceId,
      data,
    }: {
      guestId: string;
      serviceId: string;
      data: UpdateServiceStatusRequest;
    }) => updateServiceStatus(guestId, serviceId, data),
    onSuccess: (_, variables) => {
      // Invalidate specific guest user detail to refresh service status
      queryClient.invalidateQueries({
        queryKey: [GUEST_USER_DETAIL_QUERY_KEY, variables.guestId],
      });
    },
  });
};
